import React, { useState, useEffect, useRef } from 'react';
import { useAdminAuth } from '../admin/context/AdminAuthContext';
import ProductModal from './ProductModal';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { useWishlist } from '../context/WishlistContext';
import { Heart, Search, SlidersHorizontal, ChevronDown, Filter, Check } from 'lucide-react';

import { getImageUrl } from '../utils/getImageUrl';

import {
  CATEGORY_GROUPS,
  ALL_CATEGORY,
  getCategoryLabel,
  normalizeCategory,
} from '../data/categories';

// SIRALAMA MƏLUMATLARI (çoxdilli)
const SORT_OPTIONS = [
  { id: 'default',    label: { az: 'Sıralama',           en: 'Sort by',         ru: 'Сортировка',           tr: 'Sıralama' } },
  { id: 'price-asc',  label: { az: 'Qiymət: Azdan Çoxa', en: 'Price: Low–High', ru: 'Цена: по возрастанию', tr: 'Fiyat: Artan' } },
  { id: 'price-desc', label: { az: 'Qiymət: Çoxdan Aza', en: 'Price: High–Low', ru: 'Цена: по убыванию',    tr: 'Fiyat: Azalan' } },
];


// Bu bölmədəki sabit mətnlər (LanguageContext-də açarı olmayanlar)
const UI_TEXT = {
  heading:     { az: 'Seçilmiş Məhsullar', en: 'Featured Products', ru: 'Избранные товары', tr: 'Seçili Ürünler' },
  tagline:     { az: 'ZƏMANƏSİZ ZƏRİFLİK', en: 'TIMELESS ELEGANCE', ru: 'ВНЕВРЕМЕННАЯ ЭЛЕГАНТНОСТЬ', tr: 'ZAMANSIZ ZARAFET' },
  searchPlaceholder: { az: 'Məhsul axtar...', en: 'Search products...', ru: 'Поиск товаров...', tr: 'Ürün ara...' },
  empty:       { az: 'Uyğun məhsul tapılmadı.', en: 'No matching products found.', ru: 'Товары не найдены.', tr: 'Uygun ürün bulunamadı.' },
  loadMore:    { az: 'Daha Çox Göstər', en: 'Show More', ru: 'Показать ещё', tr: 'Daha Fazla Göster' },
  inStock:     { az: 'STOKDA VAR', en: 'IN STOCK', ru: 'В НАЛИЧИИ', tr: 'STOKTA VAR' },
  outOfStock:  { az: 'STOKDA YOXDUR', en: 'OUT OF STOCK', ru: 'НЕТ В НАЛИЧИИ', tr: 'STOKTA YOK' },
  noDesc:      { az: 'Təsvir əlavə edilməyib', en: 'No description added', ru: 'Описание отсутствует', tr: 'Açıklama eklenmemiş' },
  loadingProducts: { az: 'Məhsullar yüklənir...', en: 'Loading products...', ru: 'Загрузка товаров...', tr: 'Ürünler yükleniyor...' },
};

const tx = (key, lang) => UI_TEXT[key]?.[lang] || UI_TEXT[key]?.az || '';

/* ---------------- CUSTOM PINK CATEGORY DROPDOWN ---------------- */
const CategoryDropdown = ({ selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { language } = useLanguage();

  const getSelectedLabel = () => getCategoryLabel(selected, language);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 border bg-white px-4 py-2 rounded-lg text-sm text-gray-700 hover:border-pink-300 transition-all cursor-pointer shadow-sm ${
          isOpen ? 'border-pink-500 ring-2 ring-pink-100' : 'border-gray-200'
        }`}
      >
        <Filter className="w-4 h-4 text-pink-500" />
        <span className="font-medium">{getSelectedLabel()}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white border border-pink-100 rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto py-2 divide-y divide-gray-50 transition-all">
          <div className="px-1 py-1">
            <button
              onClick={() => {
                onChange('all');
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                selected === 'all'
                  ? 'bg-pink-50 text-pink-600 font-semibold'
                  : 'text-gray-700 hover:bg-pink-50/50 hover:text-pink-500'
              }`}
            >
              {ALL_CATEGORY.label[language] || ALL_CATEGORY.label.az}
              {selected === 'all' && <Check className="w-4 h-4 text-pink-500" />}
            </button>
          </div>

          {CATEGORY_GROUPS.map((group) => (
            <div key={group.id} className="px-1 py-2">
              <span className="px-3 text-[11px] font-bold tracking-wider text-pink-400 uppercase select-none block mb-1">
                ── {group.label[language] || group.label.az} ──
              </span>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    onChange(item.id);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-1.5 text-sm rounded-lg transition-colors text-left pl-5 ${
                    selected === item.id
                      ? 'bg-pink-50 text-pink-600 font-semibold'
                      : 'text-gray-600 hover:bg-pink-50/50 hover:text-pink-500'
                  }`}
                >
                  {item.label[language] || item.label.az}
                  {selected === item.id && <Check className="w-4 h-4 text-pink-500" />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- CUSTOM PINK SORT DROPDOWN ---------------- */
const SortDropdown = ({ selected, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { language } = useLanguage();

  const selectedOption = SORT_OPTIONS.find((opt) => opt.id === selected) || SORT_OPTIONS[0];
  const labelOf = (opt) => opt.label[language] || opt.label.az;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 border bg-white px-4 py-2 rounded-lg text-sm text-gray-700 hover:border-pink-300 transition-all cursor-pointer shadow-sm ${
          isOpen ? 'border-pink-500 ring-2 ring-pink-100' : 'border-gray-200'
        }`}
      >
        <SlidersHorizontal className="w-4 h-4 text-pink-500" />
        <span className="font-medium">{labelOf(selectedOption)}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-52 bg-white border border-pink-100 rounded-xl shadow-xl z-50 py-1 transition-all">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => {
                onChange(opt.id);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors text-left ${
                selected === opt.id
                  ? 'bg-pink-50 text-pink-600 font-semibold'
                  : 'text-gray-700 hover:bg-pink-50/50 hover:text-pink-500'
              }`}
            >
              {labelOf(opt)}
              {selected === opt.id && <Check className="w-4 h-4 text-pink-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ---------------- MAIN PRODUCTS COMPONENT ---------------- */
const ProductCard = ({ product, onClick }) => {
  const { language } = useLanguage();
  const { convertPrice, getCurrencySymbol } = useCurrency();
  const { wishlist, addToWishlist, removeFromWishlist } = useWishlist();

  const isFavorite = wishlist.some((item) => item.id === product.id);
  const stockCount = Number(product.stockQuantity ?? product.stock ?? 0);
  const isOutOfStock = stockCount <= 0;

  const handleWishlistClick = (e) => {
    e.stopPropagation();
    if (isFavorite) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const productName = typeof product.name === 'object' ? product.name?.[language] : product.name;
  const rawDescription = product.description || product.desc;
  const productDesc = typeof rawDescription === 'object' ? rawDescription?.[language] : rawDescription;

  const imagesList = Array.from(product.images || []);
  const mainImage = imagesList[0] || product.image || product.imageUrl;
  const hoverImage = imagesList[1];

  return (
    <div
      className={`group cursor-pointer bg-white border border-gray-100 p-4 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between relative ${
        isOutOfStock ? 'opacity-75' : ''
      }`}
      onClick={() => onClick(product)}
    >
      <div className="relative overflow-hidden bg-gray-50 mb-4 aspect-[3/4] rounded-lg">
        <img
          src={getImageUrl(mainImage)}
          alt={productName || 'product'}
          className={`w-full h-full object-cover transition-all duration-500 ${
            hoverImage ? 'group-hover:opacity-0 group-hover:scale-105' : 'group-hover:scale-105'
          }`}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/300x400?text=No+Image';
          }}
        />

        {hoverImage && (
          <img
            src={getImageUrl(hoverImage)}
            alt={`${productName} alt`}
            className="absolute inset-0 w-full h-full object-cover opacity-0 transition-all duration-500 group-hover:opacity-100 group-hover:scale-105"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/300x400?text=No+Image';
            }}
          />
        )}

        <button
          onClick={handleWishlistClick}
          className="absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors z-10"
        >
          <Heart
            className={`w-4 h-4 transition-colors ${
              isFavorite ? 'fill-pink-500 text-pink-500' : 'text-gray-600 hover:text-pink-500'
            }`}
          />
        </button>
      </div>

      <div className="flex flex-col flex-1">
        <div className="flex justify-between items-start gap-2 mb-2">
          <h3 className="text-base font-medium tracking-wide text-gray-900">{productName}</h3>

          {isOutOfStock ? (
            <span className="bg-red-50 text-red-600 border border-red-100 text-[10px] font-semibold px-2 py-0.5 rounded tracking-wider whitespace-nowrap">
              {tx('outOfStock', language)}
            </span>
          ) : (
            <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] font-medium px-2 py-0.5 rounded tracking-wider whitespace-nowrap">
              {tx('inStock', language)}
            </span>
          )}
        </div>

        {productDesc ? (
          <p className="text-xs text-gray-500 mb-3 line-clamp-2 min-h-[2rem]">{productDesc}</p>
        ) : (
          <p className="text-xs text-gray-400 italic mb-3 min-h-[2rem]">{tx('noDesc', language)}</p>
        )}

        <p className="text-lg font-serif mt-auto text-gray-900 font-semibold">
          {convertPrice(product.price)} {getCurrencySymbol()}
        </p>
      </div>
    </div>
  );
};

const Products = () => {
  const { products, loadProducts, loading } = useAdminAuth();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('default');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const [visibleCount, setVisibleCount] = useState(8);
  const { language } = useLanguage();

  // 🚀 Warning-i aradan qaldıran useEffect həlli:
  useEffect(() => {
    if (loadProducts) {
      loadProducts();
    }
  }, [loadProducts]);

  useEffect(() => {
    setVisibleCount(8);
  }, [searchTerm, sortBy, selectedCategory]);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const handleLoadMore = () => {
    setVisibleCount((prevCount) => prevCount + 4);
  };

  const filteredProducts = products
    .filter((product) => {
      const nameStr = typeof product.name === 'object' ? product.name?.[language] : product.name;
      const productName = nameStr?.toLowerCase() || '';
      const matchesSearch = productName.includes(searchTerm.toLowerCase());

      const productCategory = normalizeCategory(product.category);
      const matchesCategory = selectedCategory === 'all' || productCategory === selectedCategory;

      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'price-asc') return a.price - b.price;
      if (sortBy === 'price-desc') return b.price - a.price;
      return 0;
    });

  const visibleProducts = filteredProducts.slice(0, visibleCount);

  return (
    <section id="products" className="py-20 bg-gray-50/50">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl lg:text-4xl font-serif mb-3 text-gray-900">{tx('heading', language)}</h2>
          <p className="text-pink-500 font-medium max-w-2xl mx-auto uppercase tracking-widest text-xs">
            {tx('tagline', language)}
          </p>
        </div>

        {/* FILTERS & SEARCH BAR */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-8 bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
          <div className="relative w-full md:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-pink-400 w-4 h-4" />
            <input
              type="text"
              placeholder={tx('searchPlaceholder', language)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 transition-all"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            <CategoryDropdown selected={selectedCategory} onChange={setSelectedCategory} />
            <SortDropdown selected={sortBy} onChange={setSortBy} />
          </div>
        </div>

        {/* MƏHSULLAR SİYAHISI */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <p className="text-gray-500">
              {loading ? tx('loadingProducts', language) : tx('empty', language)}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {visibleProducts.map((product) => (
                <ProductCard key={product.id} product={product} onClick={handleProductClick} />
              ))}
            </div>

            {visibleCount < filteredProducts.length && (
              <div className="flex justify-center mt-8 mb-4">
                <button
                  onClick={handleLoadMore}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-pink-500 hover:bg-pink-600 text-white text-sm font-medium rounded-xl transition-all shadow-sm hover:shadow-pink-200 hover:shadow-lg cursor-pointer"
                >
                  {tx('loadMore', language)}
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <ProductModal product={selectedProduct} isOpen={isModalOpen} onClose={handleCloseModal} />
    </section>
  );
};

export default Products;